// ============================================================
// Add Protocol v3 wizard - reducer + screen graph
// (spec: docs/add-protocol-v3-conversational-flow-spec.md, Part 2)
//
// Pure module: no React, no I/O, no analytics (screens fire events;
// persistence lives in wizardDraft.ts). The screen graph is encoded
// in nextScreen/prevScreen so Back always lands on a real question
// (audit bug #6) and skipped screens simply never appear.
//
// Invalidation policy (D7, two-tier):
//   Structural (S2 kind, S5 branch, S1 count) - that vial's
//     downstream answers are cleared.
//   Numeric (S6 mg, S7 water, S3b amounts, S9 dose) - downstream
//     answers survive, but an edit-from-review routes through S9 so
//     the user sees the recomputed draw before returning to Review.
// ============================================================

import { FrequencyPattern, SyringeType } from '../../../data/types';
import type { InventoryItem, ProtocolPhase } from '../../../data/types';
import { uuid } from '../../../utils/uuid';
import type {
  EntryDoor,
  FastPathCandidate,
  GateKind,
  RecipeRow,
  VialSchedule,
  WizardScreen,
  WizardState,
  WizardVial,
} from './wizardTypes';

// ------------------------------------------------------------
// Construction
// ------------------------------------------------------------

export function makeEmptySchedule(): VialSchedule {
  return { frequency: null, selectedDays: [], timesOfDay: [], mealTiming: null };
}

export function makeEmptyVial(): WizardVial {
  return {
    id: uuid(),
    kind: null,
    peptideId: null,
    peptideName: null,
    recipe: [],
    recipeSource: null,
    linkedVialId: null,
    linkedVial: null,
    physicalState: null,
    mixedSelf: null,
    waterKnown: null,
    vialMgText: '',
    vialMgUnit: 'mg',
    waterMlText: '',
    doseMode: null,
    doseText: '',
    doseUnit: 'mcg',
    dosePeptideId: null,
    doseChanges: null,
    phases: null,
    schedule: makeEmptySchedule(),
    trackVial: null,
  };
}

export function makeInitialState(opts?: {
  entryDoor?: EntryDoor;
  subscriptionActive?: boolean;
}): WizardState {
  return {
    version: 1,
    screen: 's0',
    entryDoor: opts?.entryDoor ?? 'blank',
    vialCount: null,
    currentVialIndex: 0,
    vials: [],
    syringeType: null,
    protocolName: '',
    notifyEachDose: true,
    startLater: false,
    startDate: null,
    subscriptionActive: opts?.subscriptionActive ?? false,
    gate: null,
    editReturn: null,
    draftNotice: null,
    prefillLabel: null,
  };
}

// ------------------------------------------------------------
// Normalization helpers (kill the 1000x bug class at input time)
// ------------------------------------------------------------

/** Normalized vial mg for a SINGLE vial (from display state). 0 = invalid.
 *  Trusts the linked snapshot ONLY when the vial is actually linked
 *  (linkedVialId set) - a declined/backed-out candidate lingers as a
 *  back-nav marker and must not override the size the user typed. */
export function vialMg(vial: WizardVial): number {
  if (vial.linkedVialId && vial.linkedVial) return vial.linkedVial.vialSizeMg;
  const raw = parseFloat(vial.vialMgText);
  if (!(raw > 0)) return 0;
  return vial.vialMgUnit === 'mcg' ? raw / 1000 : raw;
}

/** Normalized recipe (mix vials): rows with valid mg amounts. */
export function recipeMg(vial: WizardVial): Array<{ peptideId: string; peptideName: string; amountMg: number }> {
  return vial.recipe
    .filter((r) => r.peptideId && parseFloat(r.amountText) > 0)
    .map((r) => ({
      peptideId: r.peptideId as string,
      peptideName: r.peptideName,
      amountMg: parseFloat(r.amountText),
    }));
}

/**
 * Normalized water mL. Approximate mode is ALWAYS the 2 mL default
 * (S5c "No" - the user told us they don't know; any waterMlText left
 * over from an earlier branch is stale and must not leak into dose
 * math - codex audit P0-1).
 */
export function waterMl(vial: WizardVial): number {
  // Only trust a linked vial's water when it is actually LINKED and
  // reconstituted. linkedVialId (not the mere linkedVial snapshot) is the
  // accepted-link flag: a declined/backed-out candidate lingers as a
  // back-nav marker and must not override the water the user typed. A
  // SEALED stored combo has bacWaterMl 0; the wizard collects real water at
  // S7, so fall through to the typed value (storage-mix reuse, 2026-07-10).
  if (vial.linkedVialId && vial.linkedVial && vial.linkedVial.bacWaterMl > 0) return vial.linkedVial.bacWaterMl;
  if (isApproximate(vial)) return 2;
  const raw = parseFloat(vial.waterMlText);
  return raw > 0 ? raw : 0;
}

/** C6 approximate mode: came-as-liquid and the user doesn't know the water. */
export function isApproximate(vial: WizardVial): boolean {
  return vial.physicalState === 'liquid' && vial.mixedSelf === false && vial.waterKnown === false;
}

/** C7: powder-path vials save as Stored; Start-later defaults ON. */
export function isPowderPath(vial: WizardVial): boolean {
  return vial.physicalState === 'powder';
}

/**
 * A single vial's S9 dose in mcg - THE conversion for every consumer
 * (buildProtocol, the S9 preview, and the reducer's phase-1 seeding).
 * mL doses (clinician scripts read "inject 0.05 mL"): the fraction of the
 * vial's water drawn carries the same fraction of its peptide. Requires
 * REAL water + mg - the S9 toggle hides mL for approximate vials, so this
 * math never runs against the assumed 2 mL default.
 */
export function singleDoseMcg(vial: WizardVial): number {
  const raw = parseFloat(vial.doseText);
  if (!(raw > 0)) return 0;
  if (vial.doseUnit === 'mg') return raw * 1000;
  if (vial.doseUnit === 'ml') {
    const water = waterMl(vial);
    const mg = vialMg(vial);
    if (!(water > 0) || !(mg > 0)) return 0;
    return (raw / water) * mg * 1000;
  }
  return raw;
}

// ---- start-date bounds (scheduled protocols) ----

/** Furthest-out schedulable start date: bounds notification accumulation
 *  and keeps "scheduled" meaning a real, near-term plan. */
export const MAX_START_DAYS = 90;

/** Local calendar date (yyyy-mm-dd) offset from today. Matches
 *  buildProtocol's localDateStr / schedule's parseLocalDate semantics -
 *  never UTC (a UTC parse drifts a day for evening users west of GMT). */
export function localDateWithOffset(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Reducer backstop for the S15 date picker's min/max (yyyy-mm-dd strings
 *  compare correctly as strings). Null = no date, always valid. */
function isValidStartDate(date: string | null): boolean {
  if (date === null) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  return date >= localDateWithOffset(0) && date <= localDateWithOffset(MAX_START_DAYS);
}

/**
 * Stored combos that could fulfill this recipe: a mix vial whose peptide
 * SET equals the recipe's peptide set (order-independent; per-peptide mg
 * are NOT compared - the stored vial's real recipe drives dosing and is
 * shown on the confirm screen). Excludes vials already linked to another
 * draft vial. Input order is preserved (fetchStorageMixVials sorts newest
 * first), so a single match is the freshest and 2+ go to the picker.
 * (Storage-mix wizard reuse, 2026-07-10.)
 */
export function matchStorageMixesToRecipe(
  recipePeptideIds: string[],
  storageMixes: InventoryItem[],
  usedIds: Set<string>,
): InventoryItem[] {
  const want = new Set(recipePeptideIds);
  if (want.size < 2) return [];
  return storageMixes.filter((v) => {
    if (usedIds.has(v.id)) return false;
    const have = new Set((v.mixRecipe ?? []).map((r) => r.peptideId));
    if (have.size !== want.size) return false;
    for (const id of want) if (!have.has(id)) return false;
    return true;
  });
}

// ------------------------------------------------------------
// Screen graph
// ------------------------------------------------------------

function currentVial(state: WizardState): WizardVial | undefined {
  return state.vials[state.currentVialIndex];
}

/**
 * Back target for a fast-path-linked vial reaching s8/s9. Single -> s4.
 * A linked stored combo: sealed came via s4b->s7 (back to s7),
 * already-mixed skipped straight from s4b (back to s4b).
 */
function linkedBackScreen(vial: WizardVial, isMix: boolean): WizardScreen {
  if (!isMix) return 's4';
  return vial.linkedVial && vial.linkedVial.bacWaterMl <= 0 ? 's7' : 's4b';
}

/** Does S8 still need to be asked? Once per protocol (D15). */
function syringePending(state: WizardState): boolean {
  return state.syringeType === null;
}

/**
 * The screen AFTER `screen` for the current vial, given current
 * answers. Only encodes FORWARD flow; entry into s4 is decided by the
 * fast_path_result action (async lookup), never by this function.
 */
export function nextScreen(state: WizardState, screen: WizardScreen): WizardScreen {
  const vial = currentVial(state);
  const isMix = vial?.kind === 'mix';

  switch (screen) {
    case 's0': return 's1';
    case 's1': return 's2';
    case 's2': return isMix ? 's3b' : 's3a';
    // s3a -> s4 or s5 is decided by fast_path_result.
    case 's3a': return 's5';
    case 's3b': return 's5';
    case 's4': // "Use this vial" skips S5-S7.
      return syringePending(state) ? 's8' : 's9';
    case 's4b': {
      // Sealed stored combo -> collect BAC water first (S7); an
      // already-mixed backup has a strength, skip straight to dose.
      const lv = vial?.linkedVial;
      if (lv && lv.bacWaterMl <= 0) return 's7';
      return syringePending(state) ? 's8' : 's9';
    }
    case 's5':
      return vial?.physicalState === 'powder' ? 's5powder' : 's5b';
    case 's5powder':
      return isMix ? 's7' : 's6';
    case 's5b':
      if (vial?.mixedSelf === false) return 's5c';
      return isMix ? 's7' : 's6';
    case 's5c':
      // "No" = approximate: water defaulted, skip S7.
      if (vial?.waterKnown === false) {
        if (isMix) return syringePending(state) ? 's8' : 's9';
        return 's6';
      }
      return isMix ? 's7' : 's6';
    case 's6':
      // Approximate singles already have their default water.
      if (vial && isApproximate(vial)) return syringePending(state) ? 's8' : 's9';
      return 's7';
    case 's7':
      return syringePending(state) ? 's8' : 's9';
    case 's8': return 's9';
    case 's9':
      // C5: mixes are fixed-dose in v3 - skip S10 entirely.
      return isMix ? 's11' : 's10';
    case 's10': return 's11';
    case 's11':
      // "Same schedule as X" copies times + food too - skip S12/S13.
      if (vial?.schedule.copiedFromVialIndex !== undefined) return 's14';
      return 's12';
    case 's12': return 's13';
    case 's13': return 's14';
    case 's14':
      return state.currentVialIndex < (state.vialCount ?? 1) - 1 ? 'vialDone' : 's15';
    case 'vialDone': return 's2';
    case 's15': return 's16';
    default: return screen;
  }
}

/**
 * The screen BEFORE `screen`. Mirrors nextScreen so Back retraces
 * only screens that actually appeared (audit bug #6: Back must always
 * land on a real question - the recipe stays reachable for mixes).
 */
export function prevScreen(state: WizardState, screen: WizardScreen): WizardScreen {
  const vial = currentVial(state);
  const isMix = vial?.kind === 'mix';

  switch (screen) {
    case 's1': return 's0';
    case 's2':
      return state.currentVialIndex > 0 ? 'vialDone' : 's1';
    case 's3a':
    case 's3b': return 's2';
    case 's4': return isMix ? 's3b' : 's3a';
    case 's4b':
    case 's4c': return 's3b';
    case 's5':
      if (vial?.linkedVialId === null && vial?.linkedVial) return 's4'; // declined fast path
      return isMix ? 's3b' : 's3a';
    case 's5powder': return 's5';
    case 's5b': return 's5';
    case 's5c': return 's5b';
    case 's6':
      if (vial?.physicalState === 'powder') return 's5powder';
      if (vial?.mixedSelf === false) return 's5c';
      return 's5b';
    case 's7':
      // Sealed stored combo reached S7 from the confirm screen (s4b->s7).
      if (vial?.linkedVialId && isMix) return 's4b';
      if (isMix) {
        if (vial?.physicalState === 'powder') return 's5powder';
        if (vial?.mixedSelf === false) return 's5c';
        return 's5b';
      }
      return 's6';
    case 's8': {
      // s8 follows the reconstitution end of the FIRST vial's flow.
      if (vial?.linkedVialId) return linkedBackScreen(vial, isMix);
      if (vial && isApproximate(vial)) return isMix ? 's5c' : 's6';
      return 's7';
    }
    case 's9': {
      // Syringe is asked once, in vial 0's section; later vials back
      // into the reconstitution question they actually saw.
      if (state.currentVialIndex === 0 && state.syringeType !== null) return 's8';
      if (vial?.linkedVialId) return linkedBackScreen(vial, isMix);
      if (vial && isApproximate(vial)) return isMix ? 's5c' : 's6';
      return 's7';
    }
    case 's10': return 's9';
    case 's11': return isMix ? 's9' : 's10';
    case 's12': return 's11';
    case 's13': return 's12';
    case 's14':
      if (vial?.schedule.copiedFromVialIndex !== undefined) return 's11';
      return 's13';
    case 'vialDone': return 's14';
    case 's15': return 's14';
    default: return screen;
  }
}

/**
 * Step dots: the question screens this vial's section asks, given
 * current answers (D3). Skipped steps never appear; branches not yet
 * answered assume the most common path so the count is stable within
 * a section once branches resolve.
 */
export function sectionSteps(state: WizardState, vialIndex: number): WizardScreen[] {
  const vial = state.vials[vialIndex];
  if (!vial) return [];
  const steps: WizardScreen[] = ['s2'];
  const isMix = vial.kind === 'mix';
  steps.push(isMix ? 's3b' : 's3a');
  if (vial.linkedVialId) {
    // Fast path: reconstitution questions never appear, EXCEPT a sealed
    // stored combo still collects BAC water at S7.
    if (isMix && vial.linkedVial && vial.linkedVial.bacWaterMl <= 0) steps.push('s7');
  } else {
    steps.push('s5');
    if (vial.physicalState === 'liquid') {
      steps.push('s5b');
      if (vial.mixedSelf === false) steps.push('s5c');
    }
    if (!isMix) steps.push('s6');
    if (!isApproximate(vial)) steps.push('s7');
  }
  if (vialIndex === 0) steps.push('s8');
  steps.push('s9');
  if (!isMix) steps.push('s10');
  steps.push('s11');
  if (vial.schedule.copiedFromVialIndex === undefined) steps.push('s12', 's13');
  steps.push('s14');
  return steps;
}

// ------------------------------------------------------------
// Invalidation (D7)
// ------------------------------------------------------------

/** Structural change: clear the vial's downstream answers, keep identity. */
function clearDownstreamOfLabelCheck(vial: WizardVial): WizardVial {
  return {
    ...makeEmptyVial(),
    id: vial.id,
    kind: vial.kind,
  };
}

function clearDownstreamOfPhysicalState(vial: WizardVial): WizardVial {
  return {
    ...vial,
    mixedSelf: null,
    waterKnown: null,
    vialMgText: vial.vialMgText, // amounts are identity-adjacent; numeric tier
    waterMlText: '',
    doseMode: null,
    doseText: '',
    dosePeptideId: null,
    doseChanges: null,
    phases: null,
    schedule: makeEmptySchedule(),
    trackVial: null,
  };
}

// ------------------------------------------------------------
// Actions
// ------------------------------------------------------------

export type WizardAction =
  | { type: 'start' }
  | { type: 'answer_vial_count'; count: number }
  | { type: 'confirm_reduce_vial_count'; count: number }
  | { type: 'gate_dismissed' }
  | { type: 'answer_label_check'; kind: 'single' | 'mix' }
  | { type: 'answer_peptide'; peptideId: string; peptideName: string }
  | { type: 'fast_path_result'; candidate: FastPathCandidate | null }
  | { type: 'use_existing_vial' }
  | { type: 'decline_existing_vial' }
  | { type: 'mix_fast_path_result'; candidates: FastPathCandidate[] }
  | { type: 'pick_mix_vial'; candidate: FastPathCandidate }
  | { type: 'use_existing_mix_vial' }
  | { type: 'decline_existing_mix_vial' }
  | { type: 'prefill_recipe'; source: 'wolverine' | 'glow' | 'clo' | 'custom'; rows: RecipeRow[] }
  | { type: 'add_recipe_row' }
  | { type: 'remove_recipe_row'; index: number }
  | { type: 'update_recipe_row'; index: number; row: Partial<RecipeRow> }
  | { type: 'confirm_recipe' }
  | { type: 'answer_physical_state'; state: 'powder' | 'liquid' }
  | { type: 'powder_ack'; wantsGuide: boolean }
  | { type: 'answer_mixed_self'; mixedSelf: boolean }
  | { type: 'answer_water_known'; known: boolean }
  | { type: 'set_vial_mg'; text: string; unit?: 'mg' | 'mcg' }
  | { type: 'next_from_s6' }
  | { type: 'set_water_ml'; text: string }
  | { type: 'next_from_s7' }
  | { type: 'answer_syringe'; syringeType: SyringeType }
  | { type: 'set_dose'; mode?: 'draw' | 'perPeptide'; text?: string; unit?: 'mcg' | 'mg' | 'units' | 'ml'; peptideId?: string | null }
  | { type: 'next_from_s9' }
  | { type: 'answer_dose_changes'; changes: boolean }
  | { type: 'set_phases'; phases: ProtocolPhase[] }
  | { type: 'confirm_phases' }
  | { type: 'answer_frequency'; frequency: FrequencyPattern; selectedDays?: number[] }
  | { type: 'copy_schedule_from'; vialIndex: number }
  | { type: 'set_selected_days'; days: number[] }
  | { type: 'next_from_s11' }
  | { type: 'set_times'; times: string[] }
  | { type: 'next_from_s12' }
  | { type: 'answer_meal'; mealTiming: 'fasted' | 'after_meal' | 'anytime' | null }
  | { type: 'answer_track'; track: boolean }
  | { type: 'continue_next_vial' }
  | { type: 'edit_from_review'; vialIndex: number; screen: WizardScreen }
  | { type: 'set_protocol_name'; name: string }
  | { type: 'set_notify'; on: boolean }
  | { type: 'set_start_later'; on: boolean }
  | { type: 'set_start_date'; date: string | null }
  /** S15 segmented Start control. 'pick' seeds tomorrow when no date yet. */
  | { type: 'set_start_mode'; mode: 'today' | 'pick' | 'no_date' }
  | { type: 'saved' }
  | { type: 'back' }
  | { type: 'set_subscription'; active: boolean }
  | { type: 'open_gate'; gate: GateKind }
  /** Prefill-chip "(change)" + stack-confirm navigation ONLY - normal
   *  flow must go through answer actions so validation runs. */
  | { type: 'goto_screen'; screen: WizardScreen }
  | { type: 'hydrate'; state: WizardState; notice?: string | null };

// ------------------------------------------------------------
// Reducer
// ------------------------------------------------------------

function updateVial(state: WizardState, patch: Partial<WizardVial>): WizardState {
  const vials = state.vials.slice();
  vials[state.currentVialIndex] = { ...vials[state.currentVialIndex], ...patch };
  return { ...state, vials };
}

/** Advance, honoring edit-from-review fast-forward (D7). */
function advance(state: WizardState, from: WizardScreen): WizardState {
  if (state.editReturn?.active) {
    // Numeric edits route through S9 (recomputed draw) before Review.
    if (state.editReturn.numericDirty && from !== 's9') {
      return { ...state, screen: 's9' };
    }
    return { ...state, screen: 's15', editReturn: null };
  }
  return { ...state, screen: nextScreen(state, from) };
}

function markNumericDirty(state: WizardState): WizardState {
  if (!state.editReturn?.active) return state;
  return { ...state, editReturn: { ...state.editReturn, numericDirty: true } };
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'start':
      return { ...state, screen: 's1' };

    case 'answer_vial_count': {
      if (action.count > 1 && !state.subscriptionActive) {
        return { ...state, gate: 'multi_vial' };
      }
      // Reducing below configured vials requires confirm_reduce_vial_count
      // ("Remove Vial 3 and its answers?", D7).
      if (
        state.vialCount !== null &&
        action.count < state.vials.length &&
        state.vials.slice(action.count).some(vialHasAnswers)
      ) {
        return state; // UI must confirm first
      }
      return applyVialCount(state, action.count);
    }

    case 'confirm_reduce_vial_count':
      return applyVialCount(state, action.count);

    case 'gate_dismissed':
      // Dismissal returns to the question with the selection cleared (C1).
      return { ...state, gate: null };

    case 'answer_label_check': {
      const vial = currentVial(state);
      if (!vial) return state;
      if (action.kind === 'mix' && !state.subscriptionActive) {
        return { ...state, gate: 'mix' };
      }
      let next = state;
      if (vial.kind !== null && vial.kind !== action.kind) {
        // Structural change (D7): clear this vial's downstream answers.
        next = updateVial(next, { ...clearDownstreamOfLabelCheck(vial), kind: action.kind });
        next = { ...next, editReturn: null };
      } else {
        next = updateVial(next, { kind: action.kind });
        if (state.editReturn?.active) {
          // "Edit vial" with the same answer must still reach the
          // picker / recipe (codex P1-6): walk to S3 with editReturn
          // kept so the fast-forward resumes after the identity edit.
          return { ...next, screen: nextScreen(next, 's2') };
        }
      }
      return advance(structuralAdvanceGuard(next), 's2');
    }

    case 'answer_peptide': {
      const vial = currentVial(state);
      let next = state;
      if (vial && vial.peptideId !== null && vial.peptideId !== action.peptideId) {
        // Structural change (D7): a different peptide invalidates every
        // downstream answer, including a linked-vial candidate.
        next = updateVial(next, {
          ...makeEmptyVial(),
          id: vial.id,
          kind: vial.kind,
          peptideId: action.peptideId,
          peptideName: action.peptideName,
        });
        next = { ...next, editReturn: null };
      } else {
        next = updateVial(next, {
          peptideId: action.peptideId,
          peptideName: action.peptideName,
        });
      }
      // Stay on s3a: fastPathPending tells the UI to run the <=1s
      // fast-path lookup ONCE, then fast_path_result moves on. Keying
      // the lookup on the explicit answer (not on peptideId presence)
      // is what lets Back land on s3a without being bounced forward.
      return { ...next, fastPathPending: true };
    }

    case 'fast_path_result': {
      const cleared = { ...state, fastPathPending: false };
      if (action.candidate) {
        return { ...updateVial(cleared, { linkedVial: action.candidate }), screen: 's4' };
      }
      return advance(cleared, 's3a');
    }

    case 'use_existing_vial': {
      const vial = currentVial(state);
      if (!vial?.linkedVial) return state;
      const linked = updateVial(state, {
        linkedVialId: vial.linkedVial.inventoryId,
        physicalState: 'liquid',
        mixedSelf: true,
        waterKnown: true,
      });
      return advance(linked, 's4');
    }

    case 'decline_existing_vial':
      return { ...updateVial(state, { linkedVialId: null }), screen: 's5' };

    case 'mix_fast_path_result': {
      const cleared: WizardState = { ...state, mixLookupPending: false, mixCandidates: null };
      const cands = action.candidates;
      // No stored combo matched: continue the normal build (-> s5).
      if (cands.length === 0) return advance(cleared, 's3b');
      // Exactly one: offer it on the confirm screen (linkedVialId is set
      // only when the user accepts, mirroring the single fast path).
      if (cands.length === 1) {
        return { ...updateVial(cleared, { linkedVial: cands[0] }), screen: 's4b' };
      }
      // 2+ same-peptide-set combos: never silently pick - show the picker.
      return { ...cleared, mixCandidates: cands, screen: 's4c' };
    }

    case 'pick_mix_vial':
      return {
        ...updateVial(state, { linkedVial: action.candidate }),
        mixCandidates: null,
        screen: 's4b',
      };

    case 'use_existing_mix_vial': {
      const vial = currentVial(state);
      const linked = vial?.linkedVial;
      if (!vial || !linked || !linked.mixGroupId) return state;
      // Adopt the STORED vial's real recipe: dosing must derive from it,
      // NOT the amounts the user typed to find the match (they may differ).
      const recipe: RecipeRow[] = (linked.mixRecipe ?? []).map((r) => ({
        peptideId: r.peptideId,
        peptideName: r.peptideName,
        amountText: String(r.amountMg),
      }));
      // Sealed combo (bacWaterMl 0): no strength yet -> route through S7 to
      // collect real water; do NOT stamp waterKnown. Already-mixed: adopt
      // its water and skip reconstitution like the single fast path.
      const sealed = linked.bacWaterMl <= 0;
      const next = updateVial(state, {
        linkedVialId: linked.inventoryId,
        recipe: recipe.length >= 2 ? recipe : vial.recipe,
        physicalState: sealed ? 'powder' : 'liquid',
        mixedSelf: sealed ? null : true,
        waterKnown: sealed ? null : true,
      });
      return advance(next, 's4b');
    }

    case 'decline_existing_mix_vial':
      return {
        ...updateVial(state, { linkedVialId: null, linkedVial: null }),
        mixCandidates: null,
        screen: 's5',
      };

    case 'prefill_recipe':
      return updateVial(state, { recipe: action.rows, recipeSource: action.source });

    case 'add_recipe_row': {
      const vial = currentVial(state);
      if (!vial || vial.recipe.length >= 4) return state;
      return updateVial(state, {
        recipe: [...vial.recipe, { peptideId: null, peptideName: '', amountText: '' }],
        recipeSource: vial.recipeSource ?? 'custom',
      });
    }

    case 'remove_recipe_row': {
      const vial = currentVial(state);
      if (!vial) return state;
      return markNumericDirty(updateVial(state, {
        recipe: vial.recipe.filter((_, i) => i !== action.index),
      }));
    }

    case 'update_recipe_row': {
      const vial = currentVial(state);
      if (!vial) return state;
      const recipe = vial.recipe.slice();
      recipe[action.index] = { ...recipe[action.index], ...action.row };
      const dirty = action.row.amountText !== undefined;
      const next = updateVial(state, { recipe });
      return dirty ? markNumericDirty(next) : next;
    }

    case 'confirm_recipe': {
      const vial = currentVial(state);
      if (!vial || recipeMgOf(vial) < 2) return state;
      // Already reusing a stored combo (reached s3b via Back or edit-return).
      // The recipe is fixed to the stored vial, so never re-run the lookup
      // and never walk the manual reconstitution path (s5): edit-return goes
      // back to Review, plain back-nav returns to the confirm screen (s4b).
      if (vial.linkedVialId) {
        if (state.editReturn?.active) return advance(state, 's3b');
        return { ...state, screen: 's4b' };
      }
      // Stay on s3b and flag the lookup: the UI runs the <=1s stored-combo
      // search ONCE, then dispatches mix_fast_path_result to move on
      // (mirrors answer_peptide staying on s3a).
      return { ...state, mixLookupPending: true };
    }

    case 'answer_physical_state': {
      const vial = currentVial(state);
      if (!vial) return state;
      let next = state;
      if (vial.physicalState !== null && vial.physicalState !== action.state) {
        // Structural change (D7): downstream branch answers cleared.
        next = updateVial(next, { ...clearDownstreamOfPhysicalState(vial), physicalState: action.state });
        next = { ...next, editReturn: null };
      } else {
        next = updateVial(next, { physicalState: action.state });
      }
      return advance(structuralAdvanceGuard(next), 's5');
    }

    case 'powder_ack':
      return advance({ ...state, wantsMixGuide: action.wantsGuide }, 's5powder');

    case 'answer_mixed_self': {
      const vial = currentVial(state);
      if (!vial) return state;
      let next = state;
      if (vial.mixedSelf !== null && vial.mixedSelf !== action.mixedSelf) {
        // Structural sub-branch change (D7 / codex P0-1): stale water
        // and dose answers must not survive into the other branch.
        next = updateVial(next, {
          mixedSelf: action.mixedSelf,
          waterKnown: action.mixedSelf ? true : null,
          waterMlText: '',
          doseMode: null,
          doseText: '',
          dosePeptideId: null,
          doseChanges: null,
          phases: null,
        });
        next = { ...next, editReturn: null };
      } else {
        next = updateVial(next, {
          mixedSelf: action.mixedSelf,
          waterKnown: action.mixedSelf ? true : vial.waterKnown,
        });
      }
      return advance(next, 's5b');
    }

    case 'answer_water_known': {
      const vial = currentVial(state);
      if (!vial) return state;
      let next = state;
      if (vial.waterKnown !== null && vial.waterKnown !== action.known) {
        next = updateVial(next, {
          waterKnown: action.known,
          waterMlText: '',
          doseMode: null,
          doseText: '',
          dosePeptideId: null,
          doseChanges: null,
          phases: null,
        });
        next = { ...next, editReturn: null };
      } else {
        next = updateVial(next, { waterKnown: action.known });
      }
      return advance(next, 's5c');
    }

    case 'set_vial_mg':
      return markNumericDirty(updateVial(state, {
        vialMgText: action.text,
        ...(action.unit ? { vialMgUnit: action.unit } : {}),
      }));

    case 'next_from_s6': {
      const vial = currentVial(state);
      if (!vial || vialMg(vial) <= 0) return state;
      return advance(state, 's6');
    }

    case 'set_water_ml':
      return markNumericDirty(updateVial(state, { waterMlText: action.text }));

    case 'next_from_s7': {
      const vial = currentVial(state);
      if (!vial || waterMl(vial) <= 0) return state;
      return advance(state, 's7');
    }

    case 'answer_syringe':
      return advance({ ...state, syringeType: action.syringeType }, 's8');

    case 'set_dose': {
      const vial = currentVial(state);
      if (!vial) return state;
      const patch: Partial<WizardVial> = {};
      if (action.mode !== undefined) patch.doseMode = action.mode;
      if (action.text !== undefined) patch.doseText = action.text;
      if (action.unit !== undefined) patch.doseUnit = action.unit;
      if (action.peptideId !== undefined) patch.dosePeptideId = action.peptideId;
      const next = updateVial(state, patch);
      return action.text !== undefined ? markNumericDirty(next) : next;
    }

    case 'next_from_s9': {
      const vial = currentVial(state);
      if (!vial || !(parseFloat(vial.doseText) > 0)) return state;
      let next = state;
      // Step-up singles: the S9 dose IS phase 1 (codex P0-2). Editing
      // it after building steps re-seeds phase 1 so Review and the
      // saved phases can never disagree.
      if (vial.kind !== 'mix' && vial.doseChanges && vial.phases && vial.phases.length > 0) {
        // singleDoseMcg, not an inline mg-only ternary: an mL dose would
        // otherwise seed phase 1 with the raw mL number as mcg.
        const mcg = singleDoseMcg(vial);
        const phases = vial.phases.slice();
        phases[0] = { ...phases[0], doseMcg: mcg };
        next = updateVial(next, { phases });
      }
      if (next.editReturn?.active) {
        return { ...next, screen: 's15', editReturn: null };
      }
      return { ...next, screen: nextScreen(next, 's9') };
    }

    case 'answer_dose_changes': {
      const vial = currentVial(state);
      if (!vial) return state;
      // Step-up: seed phase 1 from the S9 dose (spec: existing
      // behavior, kept). The builder then edits vial.phases in place.
      let seeded = vial.phases;
      if (action.changes && (!seeded || seeded.length === 0)) {
        // singleDoseMcg, not an inline mg-only ternary: an mL dose would
        // otherwise seed phase 1 with the raw mL number as mcg.
        const doseMcg = singleDoseMcg(vial);
        seeded = [{
          id: uuid(),
          phaseNumber: 1,
          doseMcg,
          durationValue: 4,
          durationUnit: 'weeks',
          isMaintenance: false,
          source: 'user_manual',
        }];
      }
      const next = updateVial(state, {
        doseChanges: action.changes,
        phases: action.changes ? seeded : null,
      });
      if (action.changes) return next; // stay on s10 - the step builder opens
      return advance(next, 's10');
    }

    case 'set_phases':
      return updateVial(state, { phases: action.phases });

    case 'confirm_phases': {
      const vial = currentVial(state);
      // Reducer-level contract (ported v2 guard): no empty ladders and
      // no zero-dose steps - the UI disables Next too, but the reducer
      // is the enforcement layer.
      if (!vial?.phases || vial.phases.length === 0) return state;
      if (vial.phases.some((p) => !(p.doseMcg > 0))) return state;
      return advance(state, 's10');
    }

    case 'answer_frequency': {
      const vial = currentVial(state);
      if (!vial) return state;
      return updateVial(state, {
        schedule: {
          ...vial.schedule,
          frequency: action.frequency,
          selectedDays: action.selectedDays ?? vial.schedule.selectedDays,
          copiedFromVialIndex: undefined,
        },
      });
    }

    case 'copy_schedule_from': {
      const source = state.vials[action.vialIndex];
      if (!source?.schedule.frequency) return state;
      // BY VALUE (spec S13): later edits to this vial never touch the source.
      const copied = updateVial(state, {
        schedule: {
          frequency: source.schedule.frequency,
          selectedDays: [...source.schedule.selectedDays],
          timesOfDay: [...source.schedule.timesOfDay],
          mealTiming: source.schedule.mealTiming,
          copiedFromVialIndex: action.vialIndex,
        },
      });
      return advance(copied, 's11');
    }

    case 'set_selected_days': {
      const vial = currentVial(state);
      if (!vial) return state;
      return updateVial(state, { schedule: { ...vial.schedule, selectedDays: action.days } });
    }

    case 'next_from_s11': {
      const vial = currentVial(state);
      if (!vial?.schedule.frequency) return state;
      const needsDays =
        vial.schedule.frequency === FrequencyPattern.SPECIFIC_DAYS ||
        vial.schedule.frequency === FrequencyPattern.WEEKLY;
      if (needsDays && vial.schedule.selectedDays.length === 0) return state;
      return advance(state, 's11');
    }

    case 'set_times': {
      const vial = currentVial(state);
      if (!vial) return state;
      return updateVial(state, { schedule: { ...vial.schedule, timesOfDay: action.times.slice(0, 4) } });
    }

    case 'next_from_s12': {
      const vial = currentVial(state);
      if (!vial || vial.schedule.timesOfDay.length === 0) return state;
      return advance(state, 's12');
    }

    case 'answer_meal': {
      const vial = currentVial(state);
      if (!vial) return state;
      const next = updateVial(state, { schedule: { ...vial.schedule, mealTiming: action.mealTiming } });
      return advance(next, 's13');
    }

    case 'answer_track': {
      const next = updateVial(state, { trackVial: action.track });
      const advanced = advance(next, 's14');
      // Entering Review: default Start-later ON for powder paths (C7)
      // unless the user already touched the Start control - a vial edit
      // re-enters Review through here and must not stomp an explicit
      // Today/date choice (spec 5.1: startDate survives vial edits).
      if (
        advanced.screen === 's15' &&
        advanced.vials.some(isPowderPath) &&
        !advanced.startLater &&
        !advanced.startDate &&
        !advanced.startTouched
      ) {
        return { ...advanced, startLater: true };
      }
      return advanced;
    }

    case 'continue_next_vial':
      return {
        ...state,
        currentVialIndex: state.currentVialIndex + 1,
        screen: 's2',
      };

    case 'edit_from_review':
      return {
        ...state,
        currentVialIndex: action.vialIndex,
        screen: action.screen,
        editReturn: { active: true, numericDirty: false, vialIndex: action.vialIndex },
      };

    case 'set_protocol_name':
      return { ...state, protocolName: action.name };

    case 'set_notify':
      return { ...state, notifyEachDose: action.on };

    case 'set_start_later':
      // Toggling the dateless "later" pause clears any picked date, and vice
      // versa - the two are mutually exclusive start modes.
      return { ...state, startTouched: true, startLater: action.on, startDate: action.on ? null : state.startDate };

    case 'set_start_date':
      // Picking a date supersedes the dateless pause. Out-of-bounds dates
      // (past, or beyond +MAX_START_DAYS) are ignored: the picker's min/max
      // already prevent them, this is the backstop for manual/stale input.
      if (!isValidStartDate(action.date)) return state;
      return { ...state, startTouched: true, startDate: action.date, startLater: action.date ? false : state.startLater };

    case 'set_start_mode': {
      if (action.mode === 'pick') {
        // Reveal the picker seeded with tomorrow (keep an already-picked date).
        return {
          ...state,
          startTouched: true,
          startLater: false,
          startDate: state.startDate ?? localDateWithOffset(1),
        };
      }
      return {
        ...state,
        startTouched: true,
        startLater: action.mode === 'no_date',
        startDate: null,
      };
    }

    case 'saved':
      return { ...state, screen: 's16' };

    case 'back': {
      if (state.screen === 's0' || state.screen === 's16') return state;
      if (state.editReturn?.active) {
        // Back during an edit returns to Review without applying.
        return { ...state, screen: 's15', editReturn: null };
      }
      if (state.screen === 's2' && state.currentVialIndex > 0) {
        // Back across a vial boundary re-enters the previous vial's
        // interstitial context.
        return { ...state, currentVialIndex: state.currentVialIndex - 1, screen: 'vialDone' };
      }
      // Backing out of s3a mid-lookup must not leave a live pending flag:
      // re-entering s3a later would re-fire the lookup and auto-advance.
      return { ...state, fastPathPending: false, screen: prevScreen(state, state.screen) };
    }

    case 'set_subscription':
      return { ...state, subscriptionActive: action.active };

    case 'open_gate':
      return { ...state, gate: action.gate };

    case 'goto_screen':
      return { ...state, screen: action.screen, prefillLabel: null };

    case 'hydrate': {
      // The gate is recomputed from scratch on every hydrate: a draft
      // made while subscribed must not carry gated content past a
      // now-free account (codex P1-4), and a persisted OPEN gate must
      // not survive into a now-subscribed account (live QA 2026-07-02).
      // A draft can resume after its picked start date has passed - fall
      // back to "start today" and say so, never silently keep a past date
      // (buildProtocol would save it as scheduled-in-the-past).
      const rawStartDate = action.state.startDate ?? null;
      const startDateExpired = rawStartDate !== null && rawStartDate < localDateWithOffset(0);
      const expiredNote = 'Your picked start date has passed, so this protocol is back to starting today. You can pick a new date on the review screen.';
      const notice = action.notice
        ? (startDateExpired ? `${action.notice} ${expiredNote}` : action.notice)
        : (startDateExpired ? expiredNote : null);
      const hydrated: WizardState = {
        ...action.state,
        startDate: startDateExpired ? null : rawStartDate,
        draftNotice: notice,
        gate: null,
      };
      if (!hydrated.subscriptionActive) {
        if ((hydrated.vialCount ?? hydrated.vials.length) > 1) {
          return { ...hydrated, gate: 'multi_vial' };
        }
        if (hydrated.vials.some((x) => x.kind === 'mix')) {
          return { ...hydrated, gate: 'mix' };
        }
      }
      return hydrated;
    }

    default:
      return state;
  }
}

// After a structural answer, advance() must not fast-forward to
// Review with cleared answers - cancel editReturn handled above; this
// exists as a single point if the guard ever needs more.
function structuralAdvanceGuard(state: WizardState): WizardState {
  return state;
}

function recipeMgOf(vial: WizardVial): number {
  return vial.recipe.filter((r) => r.peptideId && parseFloat(r.amountText) > 0).length;
}

function vialHasAnswers(vial: WizardVial | undefined): boolean {
  if (!vial) return false;
  return vial.kind !== null;
}

function applyVialCount(state: WizardState, count: number): WizardState {
  const vials = state.vials.slice(0, count);
  while (vials.length < count) vials.push(makeEmptyVial());
  return {
    ...state,
    vialCount: count,
    vials,
    currentVialIndex: Math.min(state.currentVialIndex, count - 1),
    gate: null,
    screen: 's2',
  };
}
